import {
  getCyclicIndex,
  useOverlappingFeaturesState,
} from 'state/useOverlappingFeaturesState';
import { useActivateOverlappingFeature } from 'features/mapping/hooks/useActivateOverlappingFeature';
import { useHistory } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { useClearMapSelectionsForRouterEntity } from 'features/mapping/hooks/useClearMapSelectionsForRouterEntity';
import { useEventListener } from 'usehooks-ts';
import { resetRouterEntitySelection } from 'features/mapping/utils/resetRouterEntitySelection';
import { OverlappingLayersNavFooter } from './OverlappingLayersNavFooter';

export const LayersNavControl = (): JSX.Element | null => {
  const { features, activeIndex, selectIndex, hasOverlappingFeatures } =
    useOverlappingFeaturesState();
  const { activateFeature } = useActivateOverlappingFeature();
  const history = useHistory();
  const clearMapSelectionsForRouterEntity =
    useClearMapSelectionsForRouterEntity();

  useEffect(() => {
    return () => {
      // Make sure to clear map selections when the component unmounts
      clearMapSelectionsForRouterEntity();
    };
  }, [clearMapSelectionsForRouterEntity]);

  const handleClearAll = useCallback((): void => {
    clearMapSelectionsForRouterEntity();
    resetRouterEntitySelection(history);
  }, [clearMapSelectionsForRouterEntity, history]);

  // Use capture phase so the handler fires before MUI's modal can stopPropagation (in other components such as drawer, dialog, etc.)
  useEventListener(
    'keydown',
    useCallback(
      (event: KeyboardEvent): void => {
        if (event?.key === 'Escape') {
          handleClearAll();
        }
      },
      [handleClearAll],
    ),
    { current: document },
    { capture: true },
  );

  const handlePrevious = (): void => {
    if (!features.length) return;
    const newIndex = getCyclicIndex({
      index: activeIndex,
      length: features.length,
      step: -1,
    });
    selectIndex(newIndex);
    activateFeature(features[newIndex]);
  };

  const handleNext = (): void => {
    if (!features.length) return;
    const newIndex = getCyclicIndex({
      index: activeIndex,
      length: features.length,
      step: 1,
    });
    selectIndex(newIndex);
    activateFeature(features[newIndex]);
  };

  if (!hasOverlappingFeatures) return null;

  return (
    <OverlappingLayersNavFooter
      activeIndex={activeIndex}
      totalCount={features.length}
      onPrevious={handlePrevious}
      onNext={handleNext}
    />
  );
};
